/* ─────────────────────────────────────────────────────────────────────────
 * PixelScreen: an inner page's title screen, cut from the same glass as the
 * home cabinet.
 *
 * The dithered sky, drifting clouds and floating island are the cabinet's
 * ScreenScene, painted at low resolution and scaled up hard. The word is
 * set in the cabinet's banded block letters (first and last a size larger),
 * which drop in on springs, bob, and hop when the cursor runs over them.
 * A caption sits above it and another below, like the year and role on
 * the home screen.
 *
 * Children render on the glass; ScreenPrompt is the "press to continue"
 * line, hung under the lower caption through --cab-prompt-top.
 * ───────────────────────────────────────────────────────────────────────── */

import type { FC, MouseEvent, ReactNode } from 'react'
import type { LetterSprite } from '@/components/landing/cabinet/pixel'

import { useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'
import { PixelIcon } from '@/components/generals/arcade'

import {
    ScreenScene,
    boldAdvanceCells,
    boldHeightCells,
    captionSprite,
    letterSprite
} from '@/components/landing/cabinet/pixel'

interface PixelScreenProps {
    word: string
    heading: string
    caption?: string
    subcaption?: string
    short?: boolean
    className?: string
    children?: ReactNode
}

interface ScreenPromptProps {
    target: string
    action: string
}

type Spring = { x: number; v: number }

type Letter = LetterSprite & {
    x: number
    base: number
    y: Spring
    hot: boolean
    release: number
}

const CAP = 1.35

const stepSpring = (s: Spring, target: number, k: number, c: number, dt: number): void => {
    s.v += (-(s.x - target) * k - s.v * c) * dt
    s.x += s.v * dt
}

export const PixelScreen: FC<PixelScreenProps> = ({
    word,
    heading,
    caption,
    subcaption,
    short = false,
    className,
    children
}): ReactNode => {
    const screenRef = useRef<HTMLDivElement>(null)
    const bgRef = useRef<HTMLCanvasElement>(null)
    const titleRef = useRef<HTMLCanvasElement>(null)

    useEffect(() => {
        const screen = screenRef.current
        const bg = bgRef.current
        const tc = titleRef.current
        const bctx = bg?.getContext('2d')
        const tctx = tc?.getContext('2d')
        if (!screen || !bg || !tc || !bctx || !tctx) return

        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        const pointer = { x: -1e6, y: -1e6, inside: false }
        const born = performance.now()
        let visible = true
        let scene: ScreenScene | null = null
        let dpr = 1
        let W = 0
        let H = 0
        let snap = 1
        let letters: Letter[] = []
        let top: HTMLCanvasElement | null = null
        let bottom: HTMLCanvasElement | null = null
        let topY = 0
        let bottomY = 0

        const layout = () => {
            const r = screen.getBoundingClientRect()
            if (r.width < 2 || r.height < 2) return
            dpr = Math.min(window.devicePixelRatio || 1, 2)
            W = Math.round(r.width * dpr)
            H = Math.round(r.height * dpr)

            // the sky: a fraction of the size, scaled up with smoothing off
            const px = Math.max(3, Math.min(6, Math.round(r.width / 260)))
            bg.width = Math.ceil(r.width / px)
            bg.height = Math.ceil(r.height / px)
            bctx.imageSmoothingEnabled = false
            scene = new ScreenScene(bg.width, bg.height)

            // the word: as wide as the glass allows, never taller than ~30% of it
            tc.width = W
            tc.height = H
            const chars = [...word.toUpperCase()]
            const big = (i: number): boolean => i === 0 || i === chars.length - 1
            const cells = chars.reduce((s, ch, i) => s + boldAdvanceCells(ch) * (big(i) ? CAP : 1), 0)
            const hCells = boldHeightCells('A')
            const portrait = r.width < r.height * 1.1
            let u = Math.floor((W * (portrait ? 0.86 : 0.7)) / cells)
            u = Math.min(u, Math.floor((H * 0.3) / (hCells * CAP)))
            u = Math.max(2, u)
            const uc = Math.round(u * CAP)
            snap = Math.max(1, Math.round(u / 2))

            top = caption ? captionSprite(caption, Math.max(2, Math.round(u * 0.6))) : null
            bottom = subcaption ? captionSprite(subcaption, Math.max(2, Math.round(u * 0.8))) : null

            const titleH = hCells * uc
            const gap = u * 3
            const block = (top ? top.height + gap : 0) + titleH + (bottom ? gap + bottom.height : 0)
            // the block rides above centre, leaving the lower glass to the island
            const start = Math.round(H * 0.38 - block / 2)
            topY = start
            const baseline = start + (top ? top.height + gap : 0) + titleH
            bottomY = baseline + gap
            const under = bottom ? bottomY + bottom.height : baseline
            const promptTop = under / dpr + Math.max(14, (u / dpr) * 4)
            screen.style.setProperty('--cab-prompt-top', `${Math.round(promptTop)}px`)

            const widths = chars.map((ch, i) => boldAdvanceCells(ch) * (big(i) ? uc : u))
            let x = Math.round((W - widths.reduce((a, b) => a + b, 0)) / 2)
            const prev = letters
            letters = chars.map((ch, i) => {
                const l: Letter = {
                    ...letterSprite(ch, big(i) ? uc : u),
                    x,
                    base: baseline,
                    y: prev[i]?.y ?? { x: reduced ? 0 : -H, v: 0 },
                    hot: false,
                    release: i * 0.07
                }
                x += widths[i]
                return l
            })
        }

        const ro = new ResizeObserver(() => layout())
        ro.observe(screen)
        layout()

        const onMove = (e: PointerEvent) => {
            const r = screen.getBoundingClientRect()
            pointer.x = (e.clientX - r.left) * dpr
            pointer.y = (e.clientY - r.top) * dpr
            pointer.inside = true
        }
        const onLeave = () => {
            pointer.inside = false
        }
        screen.addEventListener('pointermove', onMove)
        screen.addEventListener('pointerleave', onLeave)

        // pause while scrolled away
        const io = new IntersectionObserver(([e]) => {
            visible = e.isIntersecting
        })
        io.observe(screen)

        let raf = 0
        let last = performance.now()
        const tick = (now: number) => {
            raf = requestAnimationFrame(tick)
            if (!visible || document.hidden || !scene) {
                last = now
                return
            }
            const dt = Math.min(0.05, (now - last) / 1000)
            last = now
            const t = (now - born) / 1000

            scene.draw(bctx, dt, reduced ? 0 : 1)

            tctx.clearRect(0, 0, W, H)
            if (top) tctx.drawImage(top, Math.round((W - top.width) / 2), topY)
            if (bottom) tctx.drawImage(bottom, Math.round((W - bottom.width) / 2), bottomY)
            letters.forEach((l, i) => {
                if (t < l.release) return // still waiting to drop in
                const gx = l.x
                const gy = l.base - l.gh
                const idle = reduced ? 0 : Math.sin(t * 2.1 - i * 0.55) * snap * 1.2
                stepSpring(l.y, idle, 190, 11, dt)
                const over =
                    pointer.inside &&
                    pointer.x > gx &&
                    pointer.x < gx + l.gw &&
                    pointer.y > gy - l.gh * 0.25 &&
                    pointer.y < gy + l.gh
                if (over && !l.hot && !reduced) l.y.v -= snap * 2 * 24
                l.hot = over
                const y = Math.round(l.y.x / snap) * snap
                tctx.drawImage(l.canvas, gx - l.ox, gy - l.oy + y)
            })
        }
        raf = requestAnimationFrame(tick)

        return () => {
            cancelAnimationFrame(raf)
            ro.disconnect()
            io.disconnect()
            screen.removeEventListener('pointermove', onMove)
            screen.removeEventListener('pointerleave', onLeave)
        }
    }, [word, caption, subcaption])

    return (
        <div
            ref={screenRef}
            className={cn(
                'relative isolate select-none overflow-hidden bg-(--cab-cream) text-(--cab-plum)',
                'rounded-[clamp(20px,3.4vw,48px)] shadow-[0_0_0_10px_#34495a,0_0_0_14px_rgba(0,0,0,0.18),0_30px_60px_-20px_rgba(0,0,0,0.45)]',
                short ? 'h-[clamp(380px,54svh,540px)]' : 'h-[clamp(400px,64svh,620px)]',
                className
            )}
        >
            <canvas ref={bgRef} className='cab-bg' aria-hidden />
            <canvas ref={titleRef} className='cab-title' aria-hidden />
            <h1 className='cab-sr'>{heading}</h1>
            {children}
            <div className='cab-scan' aria-hidden />
            <div className='cab-glass' aria-hidden />
        </div>
    )
}

/* "Press ↓ to continue": scrolls to the section and hands it focus */
export const ScreenPrompt: FC<ScreenPromptProps> = ({ target, action }): ReactNode => {
    const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
        const el = document.getElementById(target)
        if (!el) return
        e.preventDefault()
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' })
        el.focus({ preventScroll: true })
    }

    return (
        <a href={`#${target}`} className='cab-prompt no-underline' onClick={onClick}>
            <span className='cab-blink'>
                <span className='cab-fine'>Press</span>
                <span className='cab-touch'>Tap</span>
            </span>
            <kbd className='cab-key inline-grid place-items-center'>
                <PixelIcon name='down' className='size-[1em] fill-current' />
                <span className='cab-sr'>Down</span>
            </kbd>
            <span className='cab-blink'>{action}</span>
        </a>
    )
}
